"use client"

import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { apiRequestWithAuth, getStoredToken, isTokenValid } from "@/lib/api-config"
import { useAuth } from "@/contexts/auth-context"
import { DashboardLayout } from "@/components/dashboard-layout"
import { CheckCircle as CheckCircle2 } from "@phosphor-icons/react"

interface BuyOrderVO {
  billId: number
  orderNo: string
  days: number
  price: number
  packageName: string
  payType: "wechat" | "alipay"
  payTypeLabel: string
  state: 0 | 1 | 2
  stateLabel: string
  qrCodeUrl: string
  createTime: string
  confirmTime: string | null
  shipTime: string | null
}

const PACKAGES = [
  { key: "base_monthly", group: "base", duration: "monthly", days: 30, name: "基础月卡", price: 18, durationLabel: "月",
    description: "①每日理智三清零溢出 ②日常/周常任务 ③基建收菜及高效率换班 ④公开招募/刷新 ⑤信用商店 ⑥每周剿灭" },
  { key: "base_seasonal", group: "base", duration: "seasonal", days: 90, name: "基础季卡", price: 50, durationLabel: "季",
    description: "①每日理智三清零溢出 ②日常/周常任务 ③基建收菜及高效率换班 ④公开招募/刷新 ⑤信用商店 ⑥每周剿灭" },
  { key: "base_yearly", group: "base", duration: "yearly", days: 365, name: "基础年卡", price: 188, durationLabel: "年",
    description: "①每日理智三清零溢出 ②日常/周常任务 ③基建收菜及高效率换班 ④公开招募/刷新 ⑤信用商店 ⑥每周剿灭" },
  { key: "deluxe_monthly", group: "deluxe", duration: "monthly", days: 30, name: "豪华月卡", price: 68, durationLabel: "月",
    description: "方案一全部内容 + 期间活动全勾章全奖励商店搬空毕业（危机合约/引航者/愚人节等小活动）" },
  { key: "deluxe_seasonal", group: "deluxe", duration: "seasonal", days: 90, name: "豪华季卡", price: 198, durationLabel: "季",
    description: "方案一全部内容 + 期间活动全勾章全奖励商店搬空毕业（危机合约/引航者/愚人节等小活动）" },
  { key: "deluxe_yearly", group: "deluxe", duration: "yearly", days: 365, name: "豪华年卡", price: 768, durationLabel: "年",
    description: "方案一全部内容 + 期间活动全勾章全奖励商店搬空毕业（危机合约/引航者/愚人节等小活动）" },
]

export default function UserSettings() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [panel, setPanel] = useState<"buy" | "cdk" | "account">("buy")
  const [packageGroup, setPackageGroup] = useState("base")
  const [cdk, setCdk] = useState("")
  const [cdkLoading, setCdkLoading] = useState(false)
  const [account, setAccount] = useState("")
  const [password, setPassword] = useState("")
  const [server, setServer] = useState("0")
  const [updateLoading, setUpdateLoading] = useState(false)

  const [selectedPkg, setSelectedPkg] = useState<typeof PACKAGES[number] | null>(null)
  const [payType, setPayType] = useState<"wechat" | "alipay">("wechat")
  const [submitting, setSubmitting] = useState(false)
  const [currentOrder, setCurrentOrder] = useState<BuyOrderVO | null>(null)
  const [confirming, setConfirming] = useState(false)

  const getToken = () => contextToken || getStoredToken()

  const handleCdkActivate = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({ variant: "destructive", title: "请先登录" })
      return
    }
    if (!cdk.trim()) {
      toast({ variant: "destructive", title: "请输入CDK" })
      return
    }
    setCdkLoading(true)
    try {
      const result = await apiRequestWithAuth("/useCDK", token, {
        method: "POST",
        body: JSON.stringify({ cdk: cdk.trim() }),
      }) as { code: number; msg?: string }
      if (result.code === 200) {
        toast({ variant: "success", title: "激活成功", description: "已为您续期/开通" })
        setCdk("")
      } else {
        toast({ variant: "destructive", title: "激活失败", description: result.msg || "CDK 无效或已使用" })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setCdkLoading(false)
    }
  }

  const handleBuy = async () => {
    if (!selectedPkg) {
      toast({ variant: "destructive", title: "请选择套餐" })
      return
    }
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({ variant: "destructive", title: "请先登录" })
      return
    }
    setSubmitting(true)
    try {
      const result = await apiRequestWithAuth("/buyPackage", token, {
        method: "POST",
        body: JSON.stringify({ packageKey: selectedPkg.key, payType }),
      }) as { code: number; msg?: string; data?: BuyOrderVO }
      if (result.code === 200 && result.data) {
        setCurrentOrder(result.data)
        toast({ variant: "success", title: "订单创建成功", description: "请扫码付款" })
      } else {
        toast({ variant: "destructive", title: "创建订单失败", description: result.msg || "" })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmPaid = async () => {
    if (!currentOrder) return
    const token = getToken()
    if (!token) return
    setConfirming(true)
    try {
      const result = await apiRequestWithAuth("/confirmPaid", token, {
        method: "POST",
        body: JSON.stringify({ billId: currentOrder.billId }),
      }) as { code: number; msg?: string; data?: BuyOrderVO }
      if (result.code === 200) {
        if (result.data) setCurrentOrder(result.data)
        toast({ variant: "success", title: "已通知管理员", description: "请耐心等待发货" })
      } else {
        toast({ variant: "destructive", title: "提交失败", description: result.msg || "" })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setConfirming(false)
    }
  }

  const handleUpdateAccount = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({ variant: "destructive", title: "请先登录" })
      return
    }
    if (!account.trim() || !password.trim()) {
      toast({ variant: "destructive", title: "请填写完整" })
      return
    }
    setUpdateLoading(true)
    try {
      const result = await apiRequestWithAuth("/updateAccount", token, {
        method: "POST",
        body: JSON.stringify({ account: account.trim(), password: password.trim(), server: Number.parseInt(server) }),
      }) as { code: number; msg?: string }
      if (result.code === 200) {
        toast({ variant: "success", title: "修改成功" })
      } else {
        toast({ variant: "destructive", title: "修改失败", description: result.msg || "" })
      }
    } catch {
      toast({ variant: "destructive", title: "网络错误" })
    } finally {
      setUpdateLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="compact-page compact-settings">
        <nav className="settings-segments" aria-label="设置分区">
          {([{key:"buy",label:"续费套餐"},{key:"cdk",label:"CDK 激活"},{key:"account",label:"游戏账号"}] as const).map(item =>
            <button key={item.key} aria-pressed={panel === item.key} aria-controls={`settings-${item.key}`} onClick={() => setPanel(item.key)}>{item.label}</button>
          )}
        </nav>
        <section id="settings-cdk" className="settings-body" hidden={panel !== "cdk"} aria-label="CDK 激活">
          <label htmlFor="settings-cdk-input">兑换续期或开通服务</label>
          <div className="cdk-entry"><input id="settings-cdk-input" placeholder="请输入CDK" value={cdk} onChange={e => setCdk(e.target.value)} disabled={cdkLoading} />
          <button className="compact-primary" onClick={handleCdkActivate} disabled={cdkLoading}>{cdkLoading ? "激活中..." : "激活"}</button></div>
        </section>
        <section id="settings-account" className="settings-body" hidden={panel !== "account"} aria-label="游戏账号">
          <p className="compact-muted">修改用于作战的游戏账号、密码和区服。</p>
          <div className="settings-account-fields">
            <label>游戏账号<input placeholder="新账号" value={account} onChange={e => setAccount(e.target.value)} disabled={updateLoading} autoComplete="off" /></label>
            <label>游戏密码<input placeholder="新密码" type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={updateLoading} autoComplete="new-password" /></label>
            <label>游戏区服<select value={server} onChange={e => setServer(e.target.value)} disabled={updateLoading}><option value="0">官服</option><option value="1">B服</option></select></label>
          </div>
          <button className="compact-primary settings-submit" onClick={handleUpdateAccount} disabled={updateLoading}>{updateLoading ? "修改中..." : "保存游戏账号"}</button>
        </section>
        <section id="settings-buy" className="settings-body" hidden={panel !== "buy"} aria-label="续费套餐">
          {!currentOrder || currentOrder.state >= 2 ? <>
            <div className="package-group" aria-label="套餐类型">
              {([{key:"base",label:"基础"},{key:"deluxe",label:"豪华"}]).map(item => <button key={item.key} aria-pressed={packageGroup === item.key} onClick={() => {setPackageGroup(item.key);setSelectedPkg(null)}}>{item.label}</button>)}
            </div>
            <div className="package-options">
              {PACKAGES.filter(p => p.group === packageGroup).map(pkg => <button key={pkg.key} aria-pressed={selectedPkg?.key === pkg.key} onClick={() => setSelectedPkg(pkg)}><strong>{pkg.durationLabel}卡</strong><span>{pkg.days} 天</span><b>¥{pkg.price}</b></button>)}
            </div>
            <p className="package-description">{PACKAGES.find(p => p.group === packageGroup)?.description}</p>
            {selectedPkg && <p className="selected-package">已选：{selectedPkg.name} · {selectedPkg.days} 天 · ¥{selectedPkg.price}</p>}
            <div className="payment-options" aria-label="支付方式">
              <button aria-pressed={payType === "wechat"} onClick={() => setPayType("wechat")}>微信支付</button>
              <button aria-pressed={payType === "alipay"} onClick={() => setPayType("alipay")}>支付宝</button>
            </div>
            <button className="compact-primary settings-submit" onClick={handleBuy} disabled={!selectedPkg || submitting}>{submitting ? "创建订单中..." : selectedPkg ? `提交订单 - ¥${selectedPkg.price}` : "请选择套餐"}</button>
            <p className="compact-muted payment-help">提交订单后扫码付款，再点击“我已付款”，由管理员核对发货。</p>
          </> : <>
            <div className="current-order">
              <strong>{currentOrder.state === 0 ? "请扫码付款" : "已通知管理员，请耐心等待发货"}</strong>
              {currentOrder.state === 1 && <CheckCircle2 size={18} />}
              <p>{currentOrder.packageName} · ¥{currentOrder.price}</p>
              <p className="compact-muted">订单号：{currentOrder.orderNo} · {currentOrder.stateLabel}</p>
            </div>
            {currentOrder.state === 0 && <div className="order-payment">
              <img src={currentOrder.qrCodeUrl} alt="收款码" />
              <div><p>使用 {currentOrder.payTypeLabel} 扫码付款。</p><p className="compact-muted">付款完成后再通知管理员核对。</p>
                <button className="compact-primary settings-submit" onClick={handleConfirmPaid} disabled={confirming}>{confirming ? "提交中..." : "我已付款"}</button>
              </div>
            </div>}
            <button className="settings-back" onClick={() => setCurrentOrder(null)}>返回套餐选择</button>
            <p className="compact-muted">返回不会取消已创建的订单。</p>
          </>}
        </section>
      </div>
    </DashboardLayout>
  )
}
